import { useEffect } from 'react'
import { useUser } from '@fs/zion-user'
import { usePermission } from '@fs/zion-permissions'
import { useFeatureFlag } from '@fs/zion-flags'
import getIsTWAMode from '../helpers/appMode_helper'
import useNotificationsEnabled from './useNotificationsEnabled'
import { hasSeededNotificationDefaults, markSeededNotificationDefaults } from '../helpers/twaNotifications'
import {
  getUserPrefs,
  initializeNotificationPrefs,
  PUSH_INIT_PROFILE,
  PUSH_CHANNEL,
  NOTIFICATIONS_FEATURE_FLAG,
} from '../services/notificationPrefsService'

/**
 * Seeds the user's default push subscriptions the first time they allow notifications in the app
 *
 * Scoped to the Android TWA — the same context that renders the notification settings screen — so seeding
 * and management stay aligned. Runs only once the user is signed in and OS notifications are enabled.
 *
 * IMPORTANT: `POST /prefs/initialize` is NOT idempotent — re-running it re-applies the defaults and
 * overwrites the user's push customizations. So we must only seed a genuinely first-time user. Account
 * creation seeds *email* prefs but no push, so the authoritative signal for "never seeded Together push"
 * is the absence of any {@link PUSH_CHANNEL} row in GET /prefs. The local "seeded" flag is only a cheap
 * short-circuit to avoid re-checking every launch — and it's keyed by CIS ID, because seeding is
 * per-account: multiple accounts can use the app on one device, and each needs its own defaults seeded.
 *
 * The flag is set once the account's state is settled — either it already had push prefs, or we
 * successfully seeded them. If either request fails we leave it unset so a later launch retries.
 */
export default function useSeedNotificationDefaults() {
  const { signedIn, cisId } = useUser()
  // `usePermission` returns [authorized, 'loading' | 'loaded' | 'error'] and starts at [false, 'loading'].
  // Until it reports 'loaded', `isMember` is a placeholder `false`, not an answer — seeding then would
  // hand a member the non-member profile (no Temple category) and permanently mark them seeded, which
  // `initialize` can never undo. 'error' is treated the same as 'loading': skip and retry next launch.
  const [isMember, memberPermissionStatus] = usePermission('MemberContentPermission', 'DiscoveryUserContext')
  const notificationsEnabled = useNotificationsEnabled()
  const featureEnabled = useFeatureFlag(NOTIFICATIONS_FEATURE_FLAG).isOn

  useEffect(() => {
    const canSeed =
      featureEnabled &&
      signedIn &&
      !!cisId &&
      memberPermissionStatus === 'loaded' &&
      getIsTWAMode() &&
      notificationsEnabled === true &&
      !hasSeededNotificationDefaults(cisId)

    let cancelled = false

    if (canSeed) {
      const seedIfFirstTime = async () => {
        let prefs
        try {
          const response = await getUserPrefs()
          prefs = response?.data
        } catch {
          // Couldn't read prefs — leave the flag unset so we retry on a later launch rather than risk
          // seeding blind (which could clobber an existing user).
          return
        }
        if (cancelled) return

        const alreadyHasTogetherPush = Array.isArray(prefs) && prefs.some((p) => p.channel === PUSH_CHANNEL)
        if (alreadyHasTogetherPush) {
          // Returning user — never re-seed (initialize would clobber). Short-circuit future launches.
          markSeededNotificationDefaults(cisId)
          return
        }

        const profile = isMember ? PUSH_INIT_PROFILE.member : PUSH_INIT_PROFILE.default
        try {
          await initializeNotificationPrefs(profile)
          markSeededNotificationDefaults(cisId)
        } catch {
          // Seeding failed — leave the flag unset so we retry on a later launch. GET /prefs stays the
          // authority: if the initialize actually landed server-side, the push row will be there next
          // time and we'll skip rather than clobber.
        }
      }

      seedIfFirstTime()
    }

    return () => {
      cancelled = true
    }
  }, [featureEnabled, signedIn, cisId, isMember, memberPermissionStatus, notificationsEnabled])
}
